import translate from '@translate';
import React, { FC, useEffect, useState } from 'react';
import ConfirmDialog from 'src/components/ConfirmDialog';
import { TableColumnType } from 'src/types/table';
import { getHeaderLabel, isObjectOfBooleans, sortColumns } from './functions';
import { UserSettingsType, TableColumnsSettingsType } from 'src/types/userSettings';
import FormGroup from '@mui/material/FormGroup';
import FormControlLabel from '@mui/material/FormControlLabel';
import SimpleSwitch from '../SimpleSwitch';
import AutorenewIcon from '@mui/icons-material/Autorenew';
import Button from '../Button';
import DraggableList from '../Draggable/DraggableList';
import { DropResult } from 'react-beautiful-dnd';
import { COLUMNS_ORDER_SURFIX } from './constants';
import { SwitchIcon } from '../SkladonIcon/SmallIcons';
import { colors } from 'src/themes/default';
import { styled } from '@mui/material/styles';


const ColumnsBody = styled('div')(({ theme }) => ({
    '& [data-rbd-droppable-id="show-columns-draggable"] li label.Mui-disabled.MuiFormControlLabel-labelPlacementEnd': {
        cursor: 'grab',
        color: colors.text_T1,

        '& .MuiFormControlLabel-label.Mui-disabled': {
            color: colors.text_T1,
        },
    },
}));

const ColumnItem = styled('div')(({ theme }) => ({
    minWidth: 0,
    marginTop: '4px',
    marginBottom: '8px',
    padding: '0px 5px 0px 10px',
}));

export interface ShowColumnsPropsType {
    onClose: () => void;
    visilbeColumns: TableColumnType[];
    allColumns: TableColumnType[];
    columsLabelPrefix?: string;
    userSettingsName: string;
    userSettingsData: UserSettingsType;
    editUserSettings: (newUserSettings: UserSettingsType) => void;
}

export const EDIT_KEYS = ['PID', 'DESCRIPTION'];

const ShowColumns: FC<ShowColumnsPropsType> = (props) => {
    const [visibleColumns, setVisibleColumns] = useState<TableColumnsSettingsType>({});
    const [columnsInOrder, setColumnsInOrder] = useState<TableColumnType[]>([]);
    const [editOrder, setEditOrder] = useState<boolean>(false);

    useEffect(() => {
        let newVisibleColumns: TableColumnsSettingsType = {};

        const settingsVisibleColumns = props.userSettingsData[props.userSettingsName];

        const newColumnsInOrder = sortColumns(
            props.allColumns,
            props.userSettingsData?.[`${props.userSettingsName}${COLUMNS_ORDER_SURFIX}`]
        );

        if (isObjectOfBooleans(settingsVisibleColumns)) {
            newVisibleColumns = settingsVisibleColumns;
        } else {
            newVisibleColumns = {};
            newColumnsInOrder
                .filter((column) => column.defaultShow)
                .forEach((column) => {
                    newVisibleColumns[column.key] = true;
                });
        }
        setColumnsInOrder(newColumnsInOrder);
        setVisibleColumns(newVisibleColumns);
    }, [props.userSettingsData]);

    const onChange = (column: TableColumnType, value: boolean) => {
        const newVsibleColumns = { ...visibleColumns };

        if (value) {
            newVsibleColumns[column.key] = true;
        } else {
            delete newVsibleColumns[column.key];
        }
        setVisibleColumns(newVsibleColumns);
    };

    const onDragEnd = (result: DropResult) => {
        if (!result.destination) {
            return;
        }
        const newColumnsInOrder = [...columnsInOrder];

        const fromIndex = result.source.index;
        const toIndex = result.destination?.index;
        if (
            fromIndex < 0 ||
            fromIndex >= newColumnsInOrder.length ||
            toIndex < 0 ||
            toIndex >= newColumnsInOrder.length
        ) {
            return;
        }

        const itemToMove = newColumnsInOrder.splice(fromIndex, 1)[0];
        newColumnsInOrder.splice(toIndex, 0, itemToMove);

        setColumnsInOrder(newColumnsInOrder);
    };

    const renderItem = (column: TableColumnType) => {
        const label = translate(getHeaderLabel(column, props.columsLabelPrefix));
        return (
            <FormControlLabel
                key={column.key}
                control={
                    <SimpleSwitch
                        value={!!visibleColumns[column.key] || column.allwaysShow}
                        onChange={(value) => {
                            onChange(column, value);
                        }}
                        disabled={editOrder || column.allwaysShow}
                        data-testid={`table-show-columns-switch-${column.key}`}
                    />
                }
                label={label}
            />
        );
    };

    const rendeColumns = () => {
        if (!editOrder) {
            return columnsInOrder.map((column) => <ColumnItem key={column.key}>{renderItem(column)}</ColumnItem>);
        }

        return (
            <FormGroup>
                <DraggableList
                    droppableId="show-columns-draggable"
                    onDragEnd={onDragEnd}
                    items={columnsInOrder.map((column, key) => {
                        return {
                            id: String(key),
                            element: renderItem(column),
                        };
                    })}
                />
            </FormGroup>
        );
    };

    const handleConfirm = () => {
        const newUserData = { ...(props.userSettingsData || {}) };
        newUserData[props.userSettingsName] = visibleColumns;
        newUserData[`${props.userSettingsName}${COLUMNS_ORDER_SURFIX}`] = columnsInOrder.map((item) => item.key);
        props.editUserSettings(newUserData);
    };

    const handleResetDeffault = () => {
        const newUserData = { ...(props.userSettingsData || {}) };
        delete newUserData[props.userSettingsName];
        delete newUserData[`${props.userSettingsName}${COLUMNS_ORDER_SURFIX}`];
        props.editUserSettings(newUserData);
    };

    const toggleEditOrder = () => {
        setEditOrder(!editOrder);
    };

    return (
        <ConfirmDialog
            title={
                <>
                    {translate('table.showColumns')}
                    <Button
                        tooltiop={translate('table.defaultShowColumns')}
                        onClick={handleResetDeffault}
                        sx={{
                            border: '1px solid silver',
                            float: 'right',
                            padding: '5px',
                            minWidth: 'unset',
                        }}
                        noBackground
                        variant="outlined"
                        color="inherit"
                    >
                        <AutorenewIcon />
                    </Button>
                    <Button
                        tooltiop={translate('table.editOrderColumns')}
                        onClick={toggleEditOrder}
                        sx={{
                            border: '1px solid silver',
                            float: 'right',
                            padding: '5px',
                            minWidth: 'unset',
                            marginRight: '8px',
                            marginLeft: '8px',

                            borderColor: editOrder ? colors.primary_O : 'silver',

                            '& svg path': editOrder
                                ? {
                                      fill: colors.primary_O,
                                  }
                                : {
                                      fill: colors.icon,
                                  },
                        }}
                        noBackground
                        variant="outlined"
                        color="inherit"
                    >
                        <SwitchIcon />
                    </Button>
                </>
            }
            content={<ColumnsBody>{rendeColumns()}</ColumnsBody>}
            action={handleConfirm}
            onClose={props.onClose}
            dataTestId="table-show-columns-dialog"
        />
    );
};

export default ShowColumns;
